import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { refreshSettings } from "@/hooks/useSettings";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const AdminPayment = () => {
  const [config, setConfig] = useState<any>({ sslcommerz_enabled: true, sslcommerz_sandbox: true, stripe_enabled: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("settings").select("value").eq("key", "payment_config").maybeSingle().then(({ data }) => {
      if (data?.value) setConfig(data.value);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("settings").upsert({ key: "payment_config", value: config });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Payment settings saved");
    refreshSettings();
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Payment gateway</h1>
        <p className="text-muted-foreground">Configure how customers pay</p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          SSLCommerz credentials (Store ID & Password) are stored as secure secrets.
          You can manage them in Cloud → Edge Functions → Secrets. Required keys:
          <code className="block mt-2 text-xs bg-muted p-2 rounded">SSLCOMMERZ_STORE_ID, SSLCOMMERZ_STORE_PASSWORD</code>
        </AlertDescription>
      </Alert>

      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-display text-xl font-semibold">SSLCommerz</h2>
        <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
          <div>
            <Label>Enable SSLCommerz</Label>
            <p className="text-xs text-muted-foreground">Accept payments from BD customers</p>
          </div>
          <Switch checked={config.sslcommerz_enabled} onCheckedChange={(v) => setConfig({ ...config, sslcommerz_enabled: v })} />
        </div>
        <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
          <div>
            <Label>Sandbox mode</Label>
            <p className="text-xs text-muted-foreground">Test with sandbox.sslcommerz.com</p>
          </div>
          <Switch checked={config.sslcommerz_sandbox} onCheckedChange={(v) => setConfig({ ...config, sslcommerz_sandbox: v })} />
        </div>
      </Card>

      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-display text-xl font-semibold">Stripe (fallback)</h2>
        <div className="flex items-center justify-between rounded-lg bg-secondary/50 p-3">
          <div>
            <Label>Enable Stripe</Label>
            <p className="text-xs text-muted-foreground">For international customers</p>
          </div>
          <Switch checked={config.stripe_enabled} onCheckedChange={(v) => setConfig({ ...config, stripe_enabled: v })} />
        </div>
      </Card>

      <Button variant="hero" size="lg" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save changes"}</Button>
    </div>
  );
};

export default AdminPayment;
